import type { IntercomUserType } from './IntercomUserType';
import {
  EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
  EDWARD_JONES_ORGANIZATION_NAME,
  GENWORTH_ORGANIZATION_DISPLAY_NAME,
  GENWORTH_ORGANIZATION_NAME,
  NEW_YORK_LIFE_ORGANIZATION_DISPLAY_NAME,
  NEW_YORK_LIFE_ORGANIZATION_NAME,
} from './constants';

/**
 * A capability a partner program can withhold. Each value names one surface a
 * pilot advisor may be kept out of, and is passed to `<PilotRestricted>` at the
 * point the surface is rendered.
 */
export type PilotCapability =
  | 'add-partner'
  | 'anonymous-pui'
  | 'automatic-client-results'
  | 'carrier-bga'
  | 'client-reminder'
  | 'crm-integration'
  | 'distribution-partners'
  | 'email-connection'
  | 'engagement-campaigns'
  | 'explore-quotes'
  | 'intake-form'
  | 'invite-client'
  | 'logo-branding'
  | 'org-type'
  | 'policies-comparison'
  | 'quick-start'
  | 'quote-apply';

/** Copy for a capability that takes over a whole page rather than one control. */
export interface PilotBlockedPageCopy {
  heading: string;
  body: string;
}

/**
 * Replaces the generic "Fill Intake Form" surface with a self-plan form that records a
 * compliance acknowledgement. An optional `flagKey` gates the form separately from the
 * program flag; without one it is on for every advisor in the program.
 */
export interface PilotSelfPlanIntakeForm {
  flagKey?: FeatureFlagKeys;
  acknowledgementText: string;
  /** Identifies the acknowledgement in the ConsentRecords audit trail. */
  consentId: string;
}

export interface PilotProgram {
  flagKey: FeatureFlagKeys;
  organizationDisplayName: string;
  organizationName: string;
  /** Fills the "… is preset for {label}" tooltip on locked organization fields. */
  lockedOrgLabel: string;
  intercomAdvisorType?: IntercomUserType;
  intercomClientType?: IntercomUserType;
  /** Withheld capabilities. Empty means the program pins identity only. */
  restrictedCapabilities: readonly PilotCapability[];
  restrictedTooltip: string;
  blockedPages?: Partial<Record<PilotCapability, PilotBlockedPageCopy>>;
  /** Advisor onboarding steps the program's advisors never see. */
  excludedOnboardingSteps?: readonly AdvisorOnboardingUIStep[];
  selfPlanIntakeForm?: PilotSelfPlanIntakeForm;
  /**
   * Refuse withheld capabilities at the API as well as hiding them in the UI.
   *
   * Opt-in per program rather than on for all of them. Hiding a control and refusing
   * the request behind it are different guarantees, and only a program that committed
   * to the stronger one should pay for it: turning this on for a program that has been
   * running on UI gating alone would start rejecting calls its advisors can make today.
   */
  enforceRestrictionsAtApi?: boolean;
}

export const NYL_DEFAULT_HEALTHCARE_INFLATION_PERCENT = 3;
export const NYL_DEFAULT_HEALTHCARE_INFLATION_SOURCE_TEXT =
  '2023 Center for Medicare & Medicaid Services (CMS) Office of the Actuary, National Health Expenditure Projections';
export const NYL_DEFAULT_HEALTHCARE_INFLATION_SOURCE_URL =
  'https://www.cms.gov/newsroom/press-releases/cms-office-actuary-releases-2022-2031-national-health-expenditure-projections';
export const NYL_DEFAULT_CLIENT_INTAKE_GUIDE: ClientIntakeGuideType = 'none';
export const NYL_PILOT_ADVISOR_FEATURE_FLAG = 'nylPilotAdvisor' satisfies FeatureFlagKeys;

const QUOTE_APPLY_BLOCKED_BODY =
  'To evaluate insurance options during the pilot, follow your standard processes outside of Waterlily.';

const CRM_BLOCKED_BODY =
  'To keep client records in sync during the pilot, continue using your existing CRM workflow outside of Waterlily.';

/**
 * Every partner program, in precedence order: an advisor carrying more than one
 * flag resolves to the first match here, for organization identity, Intercom
 * segmentation and capability restrictions alike.
 */
export const PILOT_PROGRAMS: readonly PilotProgram[] = [
  {
    flagKey: 'edjPilotAdvisor',
    organizationDisplayName: EDWARD_JONES_ORGANIZATION_DISPLAY_NAME,
    organizationName: EDWARD_JONES_ORGANIZATION_NAME,
    lockedOrgLabel: 'the Edward Jones pilot',
    intercomAdvisorType: 'edj_advisor',
    intercomClientType: 'edj_client',
    restrictedCapabilities: [
      'add-partner',
      'anonymous-pui',
      'client-reminder',
      'crm-integration',
      'email-connection',
      'explore-quotes',
      'intake-form',
      'invite-client',
      // Clients of a pilot advisor see the Edward Jones name, never a logo the advisor
      // uploaded themselves. Withholds the logo upload and the client-header branding
      // choice; the whitelabel organization name stays, pinned to the program's own.
      'logo-branding',
      'org-type',
      'policies-comparison',
      'quick-start',
      'quote-apply',
    ],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the Edward Jones pilot program.',
    blockedPages: {
      'crm-integration': {
        heading:
          'This feature is available on Waterlily but is not part of the Edward Jones pilot program.',
        body: CRM_BLOCKED_BODY,
      },
      'quote-apply': {
        heading: 'Quote & Apply is not available for Edward Jones pilot advisors',
        body: QUOTE_APPLY_BLOCKED_BODY,
      },
    },
    excludedOnboardingSteps: ['connect-email', 'consent'],
    selfPlanIntakeForm: {
      flagKey: 'edjAdvisorPilotIntakeForm',
      acknowledgementText:
        'By using this service, I acknowledge that I do so solely as a Waterlily client for the purpose of assessing my own needs, and not in my capacity as an Edward Jones Financial Advisor or associate.',
      consentId: 'advisorEdjSelfPlanAcknowledgement',
    },
  },
  {
    flagKey: 'genworthMode',
    organizationDisplayName: GENWORTH_ORGANIZATION_DISPLAY_NAME,
    organizationName: GENWORTH_ORGANIZATION_NAME,
    lockedOrgLabel: 'Genworth',
    restrictedCapabilities: [],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the Genworth experience.',
  },
  {
    flagKey: NYL_PILOT_ADVISOR_FEATURE_FLAG,
    organizationDisplayName: NEW_YORK_LIFE_ORGANIZATION_DISPLAY_NAME,
    organizationName: NEW_YORK_LIFE_ORGANIZATION_NAME,
    lockedOrgLabel: 'the New York Life pilot',
    intercomAdvisorType: 'nyl_advisor',
    intercomClientType: 'nyl_client',
    // New York Life withholds product recommendations, illustrations, underwriting
    // guidance, application functionality, production integrations and client edit
    // rights for the duration of the pilot.
    restrictedCapabilities: [
      'anonymous-pui',
      'automatic-client-results',
      'carrier-bga',
      'crm-integration',
      'distribution-partners',
      'email-connection',
      'engagement-campaigns',
      'explore-quotes',
      'intake-form',
      'policies-comparison',
      'quote-apply',
    ],
    restrictedTooltip:
      'This feature is available on Waterlily but is not part of the New York Life pilot program.',
    blockedPages: {
      'quote-apply': {
        heading: 'Quote & Apply is not available for New York Life pilot advisors',
        body: QUOTE_APPLY_BLOCKED_BODY,
      },
      'crm-integration': {
        heading: 'CRM integrations are not available for New York Life pilot advisors',
        body: CRM_BLOCKED_BODY,
      },
    },
    excludedOnboardingSteps: ['connect-email'],
    // New York Life committed that none of the withheld functionality is accessible,
    // which a UI-only control cannot promise: a browser holding a pre-deploy bundle
    // still renders every entry point.
    enforceRestrictionsAtApi: true,
    selfPlanIntakeForm: {
      acknowledgementText:
        'By using this service, I acknowledge that I do so solely as a Waterlily client for the purpose of assessing my own needs, and not in my capacity as a New York Life agent or associate.',
      consentId: 'advisorNylSelfPlanAcknowledgement',
    },
  },
];

/** The program an advisor belongs to, or null when they belong to none. */
export function findActivePilotProgram(
  featureFlags: FeatureFlags | undefined | null,
): PilotProgram | null {
  if (!featureFlags) {
    return null;
  }

  return PILOT_PROGRAMS.find(program => featureFlags[program.flagKey]) ?? null;
}

export function isNylPilotAdvisor(featureFlags: FeatureFlags | undefined | null): boolean {
  return findActivePilotProgram(featureFlags)?.flagKey === NYL_PILOT_ADVISOR_FEATURE_FLAG;
}

export function isPilotCapabilityRestricted(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  return !!findActivePilotProgram(featureFlags)?.restrictedCapabilities.includes(capability);
}

/**
 * Whether a request for this capability should be refused outright, rather than merely
 * hidden in the UI. True only for programs that opted into API enforcement.
 */
export function isPilotCapabilityRefusedAtApi(
  featureFlags: FeatureFlags | undefined | null,
  capability: PilotCapability,
): boolean {
  const program = findActivePilotProgram(featureFlags);

  return (
    !!program?.enforceRestrictionsAtApi && program.restrictedCapabilities.includes(capability)
  );
}

/** Onboarding steps the advisor's program waives. Empty when they belong to none. */
export function getPilotExcludedOnboardingSteps(
  featureFlags: FeatureFlags | undefined | null,
): readonly AdvisorOnboardingUIStep[] {
  return findActivePilotProgram(featureFlags)?.excludedOnboardingSteps ?? [];
}

/**
 * The self-plan intake form for this advisor, or null when their program has none or
 * the form's own gating flag is off.
 */
export function getPilotSelfPlanIntakeForm(
  featureFlags: FeatureFlags | undefined | null,
): PilotSelfPlanIntakeForm | null {
  const selfPlanIntakeForm = findActivePilotProgram(featureFlags)?.selfPlanIntakeForm;

  if (!selfPlanIntakeForm) {
    return null;
  }

  if (selfPlanIntakeForm.flagKey && !featureFlags?.[selfPlanIntakeForm.flagKey]) {
    return null;
  }

  return selfPlanIntakeForm;
}

/**
 * Partner programs that lock the advisor's organization name to a fixed value
 * (preset and non-editable during onboarding). Returns the locked names, or null
 * when the advisor's flags don't pin them to a partner organization.
 */
export function getLockedAdvisorOrgNames(
  featureFlags: FeatureFlags | undefined | null,
): { organizationDisplayName: string; organizationName: string } | null {
  const program = findActivePilotProgram(featureFlags);

  if (!program) {
    return null;
  }

  return {
    organizationDisplayName: program.organizationDisplayName,
    organizationName: program.organizationName,
  };
}
